// src/api/outboundContactWorkspaceApi.ts
//
// RTK Query client for the CRM proxy at
// `GET /api/outbound/contact-workspace/:crmContactId`.
//
// This endpoint is READ-ONLY. Response is treated as ephemeral display
// data — nothing gets written back to any CRM store. The browser never
// sees the service token; the CRM backend adds it before calling
// Outbound.
//
// Field names below mirror Outbound's projector byte-for-byte so a
// value present at Outbound's side always reaches the CRM UI intact
// (previously we shipped rename bugs — `businessName` vs `companyName`,
// `rating` vs `googleRating`, etc. — which silently dropped data).

import { baseApi } from './baseApi';

// ── Section shape (mirrors the DTO the CRM backend forwards) ──────

export type SectionStatus =
  | 'ok'
  | 'unavailable'
  | 'not_requested'
  | 'failed'
  | 'no_recent_call';

export interface OutboundContactSectionData {
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  jobTitle: string | null;
  syncedAt: string | null;
}

export interface OutboundResearchSectionData {
  profileId: string | null;
  cachedAt: string | null;
  researchedAt: string | null;
  summary: string | null;
  companyName: string | null;
  industry: string | null;
  companySize: string | null;
  location: string | null;
  website: string | null;
  painPoints: string[];
  talkingPoints: string[];
  discoveryQuestions: string[];
  socialSignals: Array<{
    kind: string;
    excerpt: string;
    url: string | null;
    date: string | null;
  }>;
  googleRating: number | null;
  googleReviewCount: number | null;
  googleListingStrength: string | null;
  // ── Phase 3 additive parity fields ──
  googleMapsUrl: string | null;
  companyFoundedYear: number | null;
  recentReviewCount90d: number | null;
  recentPositiveReviewCount90d: number | null;
  recentNegativeReviewCount90d: number | null;
  reviewActivitySignal: string | null;
  // ── Prospect Background parity fields ──
  // Outbound's contact drawer renders "Prospect Background" from the
  // research profile's businessDescription + companyAgeSummary (see
  // ProspectBackgroundSection). These are OPTIONAL here: the panel
  // renders them when the Outbound DTO carries them and degrades cleanly
  // to companyName/industry/companyFoundedYear when it doesn't.
  businessDescription?: string | null;
  companyAgeSummary?: string | null;
}

export interface OutboundLinkedInSectionData {
  cachedAt: string | null;
  confidence: 'high' | 'medium' | 'low' | 'no_match' | null;
  person: {
    fullName: string | null;
    headline: string | null;
    currentCompany: string | null;
    currentRole: string | null;
    location: string | null;
    linkedinUrl: string | null;
  } | null;
  company: {
    name: string | null;
    industry: string | null;
    companySize: string | null;
    linkedinUrl: string | null;
  } | null;
}

export interface OutboundDossierSectionData {
  dossierId: string | null;
  builtAt: string | null;
  signals: Array<{
    kind: string;
    summary: string;
    url: string | null;
    date: string | null;
  }>;
  painPoints: string[];
  talkingPoints: string[];
}

export interface OutboundCallBriefSectionData {
  summary: string | null;
  openingLine: string | null;
  painPoints: string[];
  discoveryQuestions: string[];
  objectionHandlers: Array<{ objection: string; response: string }>;
  closingPrompt: string | null;
}

export type OutboundActivityKind =
  | 'call'
  | 'email'
  | 'sms'
  | 'callback_scheduled'
  | 'callback_rescheduled'
  | 'other';

export interface OutboundActivityItem {
  id: string;
  at: string;
  kind: OutboundActivityKind;
  title: string;
  subtitle: string | null;
  actorUserId: string | null;
  details: {
    callOutcome: string | null;
    durationSec: number | null;
    hasRecording: boolean | null;
    recordingUrl: string | null;
    subject: string | null;
    bodyPreview: string | null;
    callbackAt: string | null;
  };
}

export interface OutboundNotesItem {
  id: string;
  createdAt: string;
  authorUserId: string | null;
  body: string;
}

export interface OutboundContactWorkspaceDto {
  crmContactId: string;
  outboundContactId: string;
  workspaceId: string;
  resolvedAt: string;
  source: 'outbound';
  contact: { status: SectionStatus; data: OutboundContactSectionData | null };
  research: { status: SectionStatus; data: OutboundResearchSectionData | null };
  linkedin: { status: SectionStatus; data: OutboundLinkedInSectionData | null };
  dossier: { status: SectionStatus; data: OutboundDossierSectionData | null };
  callBrief: {
    status: SectionStatus;
    sourceCallId: string | null;
    sourceCallCompletedAt: string | null;
    data: OutboundCallBriefSectionData | null;
  };
  activity: { status: SectionStatus; items: OutboundActivityItem[]; nextCursor: string | null };
  callHistory: { status: SectionStatus; items: OutboundActivityItem[] };
  notes: { status: SectionStatus; items: OutboundNotesItem[] };
  websitePreviewAvailable: boolean;
  websiteUrl: string | null;
}

export type OutboundContactWorkspaceOkResponse = { ok: true } & OutboundContactWorkspaceDto;

// ── Research trigger ──────────────────────────────────────────────

export type TriggerResearchStatus = 'completed' | 'in_progress' | 'failed';

export interface TriggerResearchArgs {
  crmContactId: string;
  force?: boolean;
}

export interface TriggerResearchResponse {
  ok: true;
  status: TriggerResearchStatus;
  profileId: string | null;
  message: string;
  retryAfterSeconds?: number;
}

// Byte-for-byte parity with Outbound's NOTE_MAX so the UI can validate
// before spending a round-trip.
export const OUTBOUND_NOTE_MAX = 5000;

export interface AddNoteArgs {
  crmContactId: string;
  body: string;
}

export interface AddNoteResponse {
  ok: true;
  note: {
    id: string;
    createdAt: string;
    authorUserId: string | null;
    body: string;
  };
}

// ── Call outcome ─────────────────────────────────────────────────

// Byte-for-byte parity with Outbound's `CallOutcome` allow-list — lets
// the UI validate the dropdown before spending a round-trip.
export const OUTBOUND_OUTCOMES = [
  'completed',
  'no_answer',
  'voicemail',
  'callback',
  'not_interested',
  'wrong_number',
] as const;
export type OutboundOutcome = (typeof OUTBOUND_OUTCOMES)[number];

export interface RecordOutcomeArgs {
  crmContactId: string;
  outcome: OutboundOutcome;
  note?: string;
}

export interface RecordOutcomeResponse {
  ok: true;
  outcome: {
    callId: string;
    outcome: OutboundOutcome;
    notes: string | null;
    completedAt: string | null;
  };
}

// ── Callback scheduling ──────────────────────────────────────────

/** Byte-for-byte parity with Outbound's `CALLBACK_MIN_LEAD_SECONDS`. */
export const OUTBOUND_CALLBACK_MIN_LEAD_SECONDS = 60;

export interface ScheduleCallbackArgs {
  crmContactId: string;
  callbackAt: string;
  note?: string;
  timezone?: string;
}

export interface ScheduleCallbackResponse {
  ok: true;
  callback: {
    id: string;
    callbackAt: string;
    status: string;
    note: string | null;
  };
}

// ── Live call status ──────────────────────────────────────────────

export type OutboundCallStatusLabel =
  | 'no_call'
  | 'queued'
  | 'ringing'
  | 'connected'
  | 'completed'
  | 'failed'
  | 'other';

export interface OutboundCallStatusDto {
  active: boolean;
  callId: string | null;
  status: OutboundCallStatusLabel;
  direction: 'outbound' | null;
  startedAt: string | null;
  durationSeconds: number | null;
  assignedUserId: string | null;
  contactId: string | null;
}

export interface GetCallStatusResponse {
  ok: true;
  status: OutboundCallStatusDto;
}

// ── Call prepare (browser calling) ────────────────────────────────

export interface PreparedCallDto {
  callId: string;
  toNumber: string;
  fromNumber: string | null;
  contactName: string | null;
  recordingMode: 'off' | 'always';
  /**
   * Phase 3B echo — the exact `WorkspaceProvider.id` Outbound
   * resolved for this call. The CRM UI verifies this against the
   * user's pre-call selection; a mismatch means Outbound honored a
   * different provider (usually because the selected id was
   * rejected) and the call is refused. Nullable in the type for
   * tolerance to older Outbound deploys that don't yet echo it —
   * current production Outbound always populates it for successful
   * provider-bound preps.
   */
  workspaceProviderId: string | null;
  /**
   * Outbound's immutable per-call recording snapshot, or null.
   *
   * Null whenever Outbound's provider-owned recording flag is off — which is
   * the case in production today — and on older Outbound deploys. The server
   * is the only consumer: it resolves the recording decision before the call
   * request and never exposes a second policy model to the browser.
   *
   * NOTE the field above it: `recordingMode` is `'off' | 'always'` and is a
   * vestigial UI/badge value. It is NOT the recording decision and shares a
   * name with the REQUEST-side mode, which uses a different vocabulary
   * ('automatic' | 'manual'). Never derive recording intent from it.
   */
  recordingPolicy: { effective?: 'automatic' | 'manual' | 'never' } | null;
}

export interface PrepareContactCallArgs {
  crmContactId: string;
  fromNumberId?: string;
}

export interface PrepareContactCallResponse {
  ok: true;
  prepared: PreparedCallDto;
}

// ── LinkedIn trigger ─────────────────────────────────────────────

export type LinkedInTriggerStatus = 'completed' | 'in_progress' | 'failed';

export interface LinkedInSnapshotEnvelope {
  cachedAt: string | null;
  confidence: 'high' | 'medium' | 'low' | 'no_match' | null;
  person: {
    fullName: string | null;
    headline: string | null;
    currentCompany: string | null;
    location: string | null;
    linkedinUrl: string | null;
  } | null;
  company: {
    name: string | null;
    industry: string | null;
    companySize: string | null;
    linkedinUrl: string | null;
  } | null;
}

export interface TriggerLinkedInArgs {
  crmContactId: string;
}

export interface TriggerLinkedInResponse {
  ok: true;
  status: LinkedInTriggerStatus;
  snapshot: LinkedInSnapshotEnvelope | null;
  message: string;
  creditsCharged?: number;
}

export const outboundContactWorkspaceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOutboundContactWorkspace: builder.query<OutboundContactWorkspaceOkResponse, string>({
      query: (crmContactId) => `/outbound/contact-workspace/${encodeURIComponent(crmContactId)}`,
    }),

    // Live call status projection. Read-only; polled from the panel with
    // visibility-aware pausing. NEVER creates local state — the DTO is
    // rendered as-is.
    getOutboundContactCallStatus: builder.query<GetCallStatusResponse, string>({
      query: (crmContactId) =>
        `/outbound/contact-workspace/${encodeURIComponent(crmContactId)}/call-status`,
    }),

    // Browser-call prepare. Writes an OutboundMessage row in OUTBOUND via
    // the internal adapter and returns { callId, toNumber, ... }. The
    // CRM Twilio Device uses these values to invoke Device.connect. No
    // CRM record is created.
    prepareOutboundContactCall: builder.mutation<
      PrepareContactCallResponse,
      PrepareContactCallArgs
    >({
      query: ({ crmContactId, fromNumberId }) => ({
        url: `/outbound/contact-workspace/${encodeURIComponent(crmContactId)}/call/prepare`,
        method: 'POST',
        body: {
          ...(typeof fromNumberId === 'string' && fromNumberId.length > 0
            ? { fromNumberId }
            : {}),
        },
      }),
    }),

    // User-initiated "Run Outbound research" trigger. Fires the CRM
    // proxy which forwards to Outbound's internal research endpoint.
    // On success, refetches the workspace query for the same contact
    // so the Research card lights up without a manual reload.
    triggerOutboundResearch: builder.mutation<
      TriggerResearchResponse,
      TriggerResearchArgs
    >({
      query: ({ crmContactId, force }) => ({
        url: `/outbound/contact-workspace/${encodeURIComponent(crmContactId)}/research`,
        method: 'POST',
        body: { force: force === true },
      }),
      onQueryStarted: async ({ crmContactId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            outboundContactWorkspaceApi.endpoints.getOutboundContactWorkspace.initiate(
              crmContactId,
              { forceRefetch: true, subscribe: false },
            ),
          );
        } catch {
          /* failure will bubble via the mutation result */
        }
      },
    }),

    // User-authored note against a synced contact. The note is stored
    // IN OUTBOUND ONLY via `LeadNote.create`. On success, refetches
    // the workspace so the Notes section renders the new item.
    addOutboundContactNote: builder.mutation<AddNoteResponse, AddNoteArgs>({
      query: ({ crmContactId, body }) => ({
        url: `/outbound/contact-workspace/${encodeURIComponent(crmContactId)}/notes`,
        method: 'POST',
        body: { body },
      }),
      onQueryStarted: async ({ crmContactId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            outboundContactWorkspaceApi.endpoints.getOutboundContactWorkspace.initiate(
              crmContactId,
              { forceRefetch: true, subscribe: false },
            ),
          );
        } catch {
          /* failure surfaces via the mutation result */
        }
      },
    }),

    // User-scheduled callback follow-up. Attaches to the latest
    // un-dispositioned call via Outbound's existing
    // `CallAssignmentService.recordOutcome({outcome:'callback'})` +
    // `scheduleCallback()` couplet — no callback logic lives on the CRM
    // side, no CRM storage. On success, refetches the workspace so the
    // callback shows up in Recent Activity.
    scheduleOutboundContactCallback: builder.mutation<
      ScheduleCallbackResponse,
      ScheduleCallbackArgs
    >({
      query: ({ crmContactId, callbackAt, note, timezone }) => ({
        url: `/outbound/contact-workspace/${encodeURIComponent(crmContactId)}/callback`,
        method: 'POST',
        body: {
          callbackAt,
          ...(typeof note === 'string' && note.length > 0 ? { note } : {}),
          ...(typeof timezone === 'string' && timezone.length > 0
            ? { timezone }
            : {}),
        },
      }),
      onQueryStarted: async ({ crmContactId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            outboundContactWorkspaceApi.endpoints.getOutboundContactWorkspace.initiate(
              crmContactId,
              { forceRefetch: true, subscribe: false },
            ),
          );
        } catch {
          /* failure surfaces via the mutation result */
        }
      },
    }),

    // User-authored call outcome. Attaches to the latest un-dispositioned
    // call for the contact via Outbound's existing
    // `CallAssignmentService.recordOutcome` (no outcome logic on the CRM
    // side, no CRM storage). On success, refetches the workspace so the
    // Recent Activity / Call History section reflects the new state.
    // User-triggered "Run LinkedIn Research" from the Contact
    // Workspace. Delegates to Outbound's existing
    // `LinkedInResearchService.generateForContact` via the CRM proxy —
    // NO CRM-side LinkedIn logic. On success, refetches the workspace
    // so the LinkedIn Research section reflects the new snapshot.
    triggerOutboundContactLinkedIn: builder.mutation<
      TriggerLinkedInResponse,
      TriggerLinkedInArgs
    >({
      query: ({ crmContactId }) => ({
        url: `/outbound/contact-workspace/${encodeURIComponent(crmContactId)}/linkedin-research`,
        method: 'POST',
        body: {},
      }),
      onQueryStarted: async ({ crmContactId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            outboundContactWorkspaceApi.endpoints.getOutboundContactWorkspace.initiate(
              crmContactId,
              { forceRefetch: true, subscribe: false },
            ),
          );
        } catch {
          /* failure surfaces via the mutation state */
        }
      },
    }),

    recordOutboundContactOutcome: builder.mutation<
      RecordOutcomeResponse,
      RecordOutcomeArgs
    >({
      query: ({ crmContactId, outcome, note }) => ({
        url: `/outbound/contact-workspace/${encodeURIComponent(crmContactId)}/outcome`,
        method: 'POST',
        body: {
          outcome,
          ...(typeof note === 'string' && note.length > 0 ? { note } : {}),
        },
      }),
      onQueryStarted: async ({ crmContactId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            outboundContactWorkspaceApi.endpoints.getOutboundContactWorkspace.initiate(
              crmContactId,
              { forceRefetch: true, subscribe: false },
            ),
          );
        } catch {
          /* failure surfaces via the mutation result */
        }
      },
    }),
  }),
});

export const {
  useGetOutboundContactWorkspaceQuery,
  useGetOutboundContactCallStatusQuery,
  useTriggerOutboundResearchMutation,
  useAddOutboundContactNoteMutation,
  useRecordOutboundContactOutcomeMutation,
  useScheduleOutboundContactCallbackMutation,
  usePrepareOutboundContactCallMutation,
  useTriggerOutboundContactLinkedInMutation,
} = outboundContactWorkspaceApi;
